// Local, content-verified cache for large model files (splat/ply/cply/sog +
// point cloud), which are served via short-lived presigned S3 URLs — a
// deliberate security fix (see git history: "Make model files private;
// serve via short-lived presigned URLs only") that closed a real access-
// control gap, but as a side effect made the browser's own HTTP cache
// useless for them: a presigned URL's signature is different on every
// request, so the browser can never recognize two requests as "the same
// resource" no matter what Cache-Control the object carries.
//
// This restores caching WITHOUT reopening that gap: every load still goes
// through the backend's canView()-gated getModel call to obtain a fresh
// presigned URL and the object's current ETag — access control is
// re-checked every single time, exactly as today. Only the big, expensive
// file transfer itself is skipped when a local copy already matches that
// fresh ETag.
const CACHE_NAME = 'cinesplat-model-cache-v1';

// Strips the query string (the presigned URL's signature, expiry, etc.) so
// repeat requests for the same underlying S3 object always resolve to the
// same cache key, even though the signed URL handed out is different every
// time.
function stableCacheKey(url) {
  return url.split('?')[0];
}

/**
 * Fetches `url`, transparently serving a cached copy instead when one exists
 * under the same stable key (URL path, ignoring the presigned query string)
 * AND its own ETag (read directly from the cached response's real S3
 * headers — S3 returns ETag on every GetObject) matches the `etag` just
 * given. Falls back to a plain fetch whenever caching isn't possible or
 * fails for any reason (Cache API unavailable — some private-browsing
 * modes restrict it —, quota exceeded, etc.) — caching is always a pure
 * optimization here, never a requirement for a load to succeed.
 *
 * @param {string} url - presigned GET URL
 * @param {string | null | undefined} etag - the object's CURRENT ETag, from
 *   this same request's own getModel call (i.e. already access-control
 *   checked) — not trusted from any older/cached source.
 * @returns {Promise<Response>}
 */
export async function fetchWithModelCache(url, etag) {
  if (!etag || typeof caches === 'undefined') {
    return fetch(url);
  }

  const cacheKey = stableCacheKey(url);
  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(cacheKey);
    if (cached && cached.headers.get('etag') === etag) {
      return cached;
    }

    const response = await fetch(url);
    if (response.ok) {
      // Cache a clone — the original body still gets consumed by the
      // caller's own stream reader. Best-effort: a quota/storage failure
      // here must never break the actual load.
      cache.put(cacheKey, response.clone()).catch(() => {});
    }
    return response;
  } catch {
    return fetch(url);
  }
}
